import axiosInstance from '../../api/axios';

// Types mirror the new `/api/analytics/scope/*` endpoints verbatim — see
// specs/20-contracts/feedback-round-2026-08.md (the locked contract both the
// backend and frontend builders build against for this feedback round). Do
// not rename fields without updating both sides of the contract. Shared
// across every persona dashboard (not persona-scoped like controller/cae/etc).

export type ScopeRiskLevelCount = { level: string; count: number };

/** GET /api/analytics/scope/control-counts */
export type ScopeControlCountsResponse = {
  total: number;
  key_count: number;
  non_key_count: number;
  by_risk_level: ScopeRiskLevelCount[];
  prev_total: number | null;
  delta: number | null;
};

/** GET /api/analytics/scope/risk-summary */
export type ScopeRiskSummaryResponse = {
  total: number;
  by_level: ScopeRiskLevelCount[];
  mapped_count: number;
  unmapped_count: number;
  prev_total: number | null;
  delta: number | null;
};

/** GET /api/analytics/scope/systems-in-scope */
export type ScopeSystemRow = { id: string; name: string; category: string | null };
export type ScopeSystemsResponse = { total: number; systems: ScopeSystemRow[] };

/** GET /api/analytics/scope/entities-in-scope */
export type ScopeEntityRow = { id: string; name: string; entity_type: string | null };
export type ScopeEntitiesResponse = { total: number; entities: ScopeEntityRow[] };

/**
 * GET /api/analytics/scope/materiality — the exact numeric threshold field
 * name(s) vary with the `materialityassessments` schema (per the contract,
 * the backend "surfaces the numeric threshold(s) + benchmark actually
 * present"), so this stays a loose record beyond the guaranteed fields
 * rather than guessing column names. The table widget renders any extra
 * numeric/string keys generically.
 */
export type ScopeMaterialityAssessment = {
  id: string;
  cycle_name: string | null;
  benchmark: string | null;
  status: string | null;
  updated_at: string | null;
  [key: string]: unknown;
};
export type ScopeMaterialityResponse = { assessments: ScopeMaterialityAssessment[] };

/** GET /api/analytics/scope/planned-vs-completed */
export type ScopePlannedCompletedResponse = { planned: number; completed: number; completion_pct: number };

/** GET /api/analytics/scope/overdue-summary */
export type ScopeOverdueTestingRow = {
  test_run_id: string;
  control_id: string;
  control_title: string;
  assignee_name: string | null;
  due_date: string;
  days_overdue: number;
};
export type ScopeOverduePbcRow = {
  id: string;
  title: string;
  control_id: string;
  assigned_to_name: string | null;
  due_date: string;
  days_overdue: number;
};
export type ScopeOverdueSummaryResponse = {
  testing_overdue: number;
  pbc_overdue: number;
  testing: ScopeOverdueTestingRow[];
  pbc: ScopeOverduePbcRow[];
};

/** GET /api/analytics/scope/testing-status-rollup */
export type ScopeStatusCount = { status: string; count: number };
export type ScopeTestingStatusRollupResponse = { total: number; by_status: ScopeStatusCount[] };

const BASE = '/api/analytics/scope';

export const fetchScopeControlCounts = async (): Promise<ScopeControlCountsResponse> => {
  const response = await axiosInstance.get<ScopeControlCountsResponse>(`${BASE}/control-counts`);
  return (
    response?.data ?? { total: 0, key_count: 0, non_key_count: 0, by_risk_level: [], prev_total: null, delta: null }
  );
};

export const fetchScopeRiskSummary = async (): Promise<ScopeRiskSummaryResponse> => {
  const response = await axiosInstance.get<ScopeRiskSummaryResponse>(`${BASE}/risk-summary`);
  return (
    response?.data ?? { total: 0, by_level: [], mapped_count: 0, unmapped_count: 0, prev_total: null, delta: null }
  );
};

export const fetchScopeSystems = async (): Promise<ScopeSystemsResponse> => {
  const response = await axiosInstance.get<ScopeSystemsResponse>(`${BASE}/systems-in-scope`);
  return response?.data ?? { total: 0, systems: [] };
};

export const fetchScopeEntities = async (): Promise<ScopeEntitiesResponse> => {
  const response = await axiosInstance.get<ScopeEntitiesResponse>(`${BASE}/entities-in-scope`);
  return response?.data ?? { total: 0, entities: [] };
};

export const fetchScopeMateriality = async (): Promise<ScopeMaterialityResponse> => {
  const response = await axiosInstance.get<ScopeMaterialityResponse>(`${BASE}/materiality`);
  return response?.data ?? { assessments: [] };
};

export const fetchScopePlannedCompleted = async (): Promise<ScopePlannedCompletedResponse> => {
  const response = await axiosInstance.get<ScopePlannedCompletedResponse>(`${BASE}/planned-vs-completed`);
  return response?.data ?? { planned: 0, completed: 0, completion_pct: 0 };
};

export const fetchScopeOverdueSummary = async (): Promise<ScopeOverdueSummaryResponse> => {
  const response = await axiosInstance.get<ScopeOverdueSummaryResponse>(`${BASE}/overdue-summary`);
  return response?.data ?? { testing_overdue: 0, pbc_overdue: 0, testing: [], pbc: [] };
};

export const fetchScopeTestingStatusRollup = async (): Promise<ScopeTestingStatusRollupResponse> => {
  const response = await axiosInstance.get<ScopeTestingStatusRollupResponse>(`${BASE}/testing-status-rollup`);
  return response?.data ?? { total: 0, by_status: [] };
};
